"use client";

import React, { useState, useEffect, useRef } from "react";
import * as AriaKit from "@ariakit/react";
import Button from "./Button";
import Icon from "../../common/components/Icon";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";

export interface ModalProps {
  open: boolean;
  icon?: string;
  hasCloseBtn?: boolean;
  actions?: React.ReactNode /* @deprecated Add the Button to the bottom of children, use ModalActions component for stickyness */;
  title: string | React.ReactNode;
  stickyActions?: boolean;
  wide?: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  style?: any;
  ref?: any;
  hideOnInteractOutside?: boolean;
}

const ModalActions: React.FC<{
  sticky: boolean;
  children: React.ReactNode;
}> = ({ children, sticky }) => {
  return (
    <div
      className={`z-4 bottom-0 start-0 p-3 end-0 color-bg-white py-2 w-100 shadow-top ${sticky ? "position-sticky" : ""}`}
    >
      {children}
    </div>
  );
};

const Modal: React.FC<ModalProps> = React.forwardRef<any, ModalProps>(
  (
    {
      open,
      icon,
      onClose,
      actions,
      wide,
      children,
      title,
      style,
      hideOnInteractOutside,
    },
    ref,
  ) => {
    const t = useTranslations();
    const pathname = usePathname();

    // Detect router-based modals (intercepted routes)
    const isRouterModal =
      pathname.includes("/@modal/") ||
      (onClose && onClose.toString().includes("router.back"));

    // For router modals, manage state to allow close animations
    const [internalOpen, setInternalOpen] = useState(open);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (open) {
        setInternalOpen(true);
      }
    }, [open]);

    // Enable smooth height transitions for dynamic content
    useEffect(() => {
      const content = contentRef.current;
      if (!content) return;

      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { height } = entry.contentRect;
          // Apply smooth height transition via CSS custom property
          content.style.setProperty("--content-height", `${height}px`);
        }
      });

      observer.observe(content);
      return () => observer.disconnect();
    }, []);

    const handleClose = () => {
      if (isRouterModal) {
        // For router modals, delay the router.back() to allow animation
        setInternalOpen(false);
        setTimeout(() => {
          onClose?.();
        }, 150); // Match CSS dialog transition duration
      } else {
        // For regular modals, close immediately
        onClose?.();
      }
    };

    const dialog = AriaKit.useDialogStore({
      open: isRouterModal ? internalOpen : open,
      setOpen: (isOpen) => {
        if (!isOpen) {
          handleClose();
        }
      },
      animated: 250, // Match CSS transition duration
    });

    return (
      <AriaKit.Dialog
        store={dialog}
        backdrop={<div className="backdrop" />}
        className={`dialog ${wide ? "dialog-wide" : ""}`}
        portal
        getPersistentElements={() => document.querySelectorAll(".toaster")}
        style={style}
        hideOnInteractOutside={hideOnInteractOutside}
        autoFocusOnShow={false}
      >
        <div
          className="scrollbar-thin overflow-y-auto overflow-x-hidden"
          ref={ref}
        >
          <div className="position-sticky p-3 px-lg-4 top-0 color-bg-white shadow-and-blur-on-scroll z-3 border-bottom">
            <div className="d-flex justify-content-between gap-2 align-items-center">
              <h6 className="d-flex align-items-center gap-2 my-0 fw-semibold dialog-title">
                {icon && <Icon icon={icon} className="hide-on-mobile" />}
                {title}
              </h6>
              <Button
                appearance="link"
                icon="close"
                iconPosition="right"
                className="fs-8 color-primary-dimmed"
                onClick={handleClose}
              >
                <span className="hide-on-mobile">{t("close")}</span>
              </Button>
            </div>
          </div>
          <div
            className="p-3 px-lg-4 modal-content-container"
            ref={contentRef}
            style={{
              transition: "min-height 300ms cubic-bezier(0.4, 0, 0.2, 1)",
              minHeight: "var(--content-height, auto)",
            }}
          >
            {children}
          </div>
          {actions && <ModalActions sticky={true}>{actions}</ModalActions>}
        </div>
      </AriaKit.Dialog>
    );
  },
);

Modal.displayName = "Modal";

export { ModalActions };

export default Modal;
